// MACHINERY V2 (Stroje) — kebab action set + permission gating (M2.2).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §10 + data package §9 "Actions".
//
// ONE PLACE decides which rows a given user sees on a given machine, so the
// gate cannot drift between the card, a future row (M2.3) and the profile
// (M2.4). `canWrite` is `usePermission("Machinery").canWrite` on every branch
// below. That is NOT blanket parity with live: QR matches live's guard exactly,
// while Obnoviť is deliberately STRICTER than live's ungated button. The
// branch-by-branch block further down names both, and cites the server
// attribute that settles the second.
//
//   Read  (Workers included) : Profil · Vytvoriť servisnú správu
//   Write                    : QR kód · Vytvoriť servisnú žiadosť ·
//                              Upraviť detaily · Duplikovať ·
//                              Priradiť do skupiny · Vyradiť · Obnoviť
//
// >>> QR IS Machinery:WRITE (SEC-REQ-B7-12). IT IS NOT A READ ACTION. <<<
// MachineQRCodeDialog reads the machine's digital-nameplate id-link from the
// Machinery:Write nameplate GET; a Read-level link endpoint was REJECTED by
// Security (SEC-REQ-B7-34 option (i)). Ungating QR therefore hands a Worker a
// 403 and an empty dialog. EVERY live surface wraps it in
// PermissionGuard(Machinery, Write) with a "Do not ungate" note —
// components/CompactMachineCard.tsx:409, components/Machine.tsx:541,
// components/RedesignedMachineCard.tsx:251 and :452 (the guard lines, not the
// "Do not ungate" comment block that precedes each one).
//
// WHERE v2 LANDS AGAINST LIVE — EXACTLY, BRANCH BY BRANCH:
//  · QR is Write on BOTH branches below. On the ACTIVE branch that is straight
//    parity with all four live sites. On the DECOMMISSIONED branch it matches
//    the live PROFILE ONLY: Machine.tsx:541 is the single live site that still
//    renders QR on a retired machine (its guard sits outside the
//    `!isDecommissioned` test that hides Edit / Report problem / Create SR).
//    The live CARDS show NO QR there at all — CompactMachineCard.tsx and both
//    RedesignedMachineCard.tsx sites take an `isDecommissioned ? Obnoviť :
//    <>…QR…</>` fork, so their retired menu is Profil + Obnoviť. v2 follows
//    the profile deliberately; the row is Write-gated either way, so no Worker
//    gains anything live withholds.
//  · Profil is READ-level on BOTH branches, which is exact CARD parity: every
//    card site renders it as the FIRST MenuItem, unconditional, outside the
//    isDecommissioned fork and with no PermissionGuard —
//    components/CompactMachineCard.tsx:397 and
//    components/RedesignedMachineCard.tsx:243 / :444.
//  · Obnoviť (restore) is Write in v2 and UNGATED on ALL FOUR live surfaces.
//    THIS DIVERGENCE IS DELIBERATE AND APPROVED, and v2 is the correct surface,
//    because restore is Write-only ON THE SERVER: GoToStockServer-machinery →
//    src/GoToStock/Machinery/GoToStock.Machinery.WebApi/Areas/Customer/
//    Contollers/CustomerMachinesController.cs:364-365 carries
//    [HttpPost("{id}/restore")] +
//    [RequireModulePermission(Modules.Machinery, PermissionLevel.Write)].
//    Live renders the control under no PermissionGuard at all —
//    components/CompactMachineCard.tsx:401-403,
//    components/RedesignedMachineCard.tsx:245-247 and :446-448, and
//    components/Machine.tsx:436-456 (the decommission banner's button) — so
//    every Worker who clicks it gets a 403. That is a logged LIVE defect
//    awaiting follow-up; do NOT "restore parity" by ungating v2.
//
// A decommissioned machine therefore collapses to Profil · QR · Obnoviť for a
// Write user and to Profil alone for a Worker — never to an empty list, so the
// fleet kebab always renders. FleetCardMenu's `actions.length === 0` early
// return still fires, but only for its OTHER consumer, the profile, which
// filters Profil out as self-referential (see profile/useProfileActions.ts).
//
// NOTE FOR THE NEXT READER: ticket §10's permission matrix still lists QR as
// visible to all roles. That ROW IS STALE against SEC-REQ-B7-12 and is being
// corrected at wave end — do not re-introduce Read-level QR from it.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import { Machine } from "../../models/models";
import { FleetMenuAction } from "./FleetCardMenu";

export interface FleetActionHandlers {
  onProfile: (m: Machine) => void;
  onQrCode: (m: Machine) => void;
  onCreateServiceReport: (m: Machine) => void;
  onCreateServiceRequest: (m: Machine) => void;
  onEditDetails: (m: Machine) => void;
  onDuplicate: (m: Machine) => void;
  onAssignGroup: (m: Machine) => void;
  onDecommission: (m: Machine) => void;
  onRestore: (m: Machine) => void;
}

export const buildFleetActions = (
  machine: Machine,
  canWrite: boolean,
  h: FleetActionHandlers
): FleetMenuAction[] => {
  if (machine.isDecommissioned) {
    // PROFIL IS READ-LEVEL AND UNCONDITIONAL, HERE TOO. Both live cards render
    // it as the FIRST MenuItem, outside their isDecommissioned fork and with no
    // PermissionGuard — components/CompactMachineCard.tsx:397 and
    // components/RedesignedMachineCard.tsx:243 / :444. Folding it into the
    // canWrite block below would strip a Worker's only kebab route into a
    // retired machine's profile, which live gives them.
    const decommissioned: FleetMenuAction[] = [
      { id: "profile", labelKey: "myMachinery:profile", onSelect: () => h.onProfile(machine) },
    ];
    // QR is Write here too (SEC-REQ-B7-12) — see the header. A Worker's list
    // therefore stops at Profil; only a Write user also gets QR + Obnoviť.
    if (canWrite) {
      decommissioned.push(
        { id: "qr", labelKey: "myMachinery:qrCode", onSelect: () => h.onQrCode(machine) },
        {
          id: "restore",
          labelKey: "myMachinery:restore",
          onSelect: () => h.onRestore(machine),
        }
      );
    }
    return decommissioned;
  }

  const actions: FleetMenuAction[] = [
    { id: "profile", labelKey: "myMachinery:profile", onSelect: () => h.onProfile(machine) },
  ];

  // Write-gated (SEC-REQ-B7-12), but pushed HERE rather than with the other
  // Write rows below so the menu keeps the live surfaces' order:
  // Profil · QR · Vytvoriť servisnú správu · ...
  if (canWrite) {
    actions.push({
      id: "qr",
      labelKey: "myMachinery:qrCode",
      onSelect: () => h.onQrCode(machine),
    });
  }

  actions.push({
    id: "sr",
    labelKey: "myMachinery:createServiceReport",
    onSelect: () => h.onCreateServiceReport(machine),
  });

  if (!canWrite) return actions;

  actions.push(
    {
      id: "rfs",
      labelKey: "myMachinery:createServiceRequest",
      onSelect: () => h.onCreateServiceRequest(machine),
    },
    {
      id: "edit",
      labelKey: "myMachinery:editDetails",
      onSelect: () => h.onEditDetails(machine),
    },
    {
      id: "duplicate",
      labelKey: "myMachinery:duplicate",
      onSelect: () => h.onDuplicate(machine),
    },
    {
      id: "group",
      labelKey: "myMachinery:assignToGroup",
      onSelect: () => h.onAssignGroup(machine),
    },
    {
      id: "decommission",
      labelKey: "myMachinery:decommission",
      onSelect: () => h.onDecommission(machine),
      danger: true,
    }
  );

  return actions;
};

export default buildFleetActions;
